import React, { useEffect, useState, useRef } from 'react';
// Note: Assuming './StudentCarousel.css' is the file that contains the CSS you provided
import './StudentCarousel.css';

const PosterCarousel = () => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isScrolling, setIsScrolling] = useState(false);
    const carouselContainerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    
    const posters = [
        {
            image: '/posters/ACHI1.jpg'
        },
        {
            image: '/posters/ACHI2.jpg'
        },
        {
            image: '/posters/ACHI3.jpg'
        },
        {
            image: '/posters/ACHI4.jpg'
        },
        {
            image: '/posters/ACHI5.jpg'
        },
        {
            image: '/posters/ACHI6.jpg'
        },
    ];

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle manual scroll interactions
    const handleScrollStart = () => {
        setIsScrolling(true);
        const trackElements = document.querySelectorAll('.carousel-track');
        trackElements.forEach(track => {
            track.style.animationPlayState = 'paused';
        });
    };

    const handleScrollEnd = () => {
        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        
        // Set a timeout to resume animation after scrolling stops
        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
            const trackElements = document.querySelectorAll('.carousel-track');
            trackElements.forEach(track => {
                track.style.animationPlayState = 'running';
            });
        }, 2000); // Resume auto-scroll after 2 seconds of inactivity
    };

    // Add event listeners for scroll interactions
    useEffect(() => {
        const container = carouselContainerRef.current;
        if (container) {
            container.addEventListener('mousedown', handleScrollStart);
            container.addEventListener('touchstart', handleScrollStart, { passive: true });
            container.addEventListener('mouseup', handleScrollEnd);
            container.addEventListener('touchend', handleScrollEnd);
            container.addEventListener('mouseleave', handleScrollEnd);
            
            // Make sure to clean up the event listeners
            return () => {
                container.removeEventListener('mousedown', handleScrollStart);
                container.removeEventListener('touchstart', handleScrollStart);
                container.removeEventListener('mouseup', handleScrollEnd);
                container.removeEventListener('touchend', handleScrollEnd);
                container.removeEventListener('mouseleave', handleScrollEnd);
                
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
            };
        }
    }, []);

    // Calculate poster size based on window width
    const getPosterSize = () => {
        if (windowWidth <= 380) return 'calc(100vw - 24px)';
        if (windowWidth <= 480) return 'calc(100vw - 32px)';
        if (windowWidth <= 768) return 'calc(100vw - 40px)';
        return '574px';
    };

    const renderPosterCards = (postersData) => {
        // Always duplicate the same amount for consistent behavior
        const duplicateTimes = 4;
        
        // Create duplicated array for infinite loop effect
        let duplicatedPosters = [];
        for (let i = 0; i < duplicateTimes; i++) {
            duplicatedPosters = [...duplicatedPosters, ...postersData];
        }
        
        const posterSize = getPosterSize();
        
        return (
            <div className="carousel-track">
                {duplicatedPosters.map((poster, index) => (
                    <div 
                        className="poster-card" 
                        key={`poster-${index}`}
                        style={{
                            minWidth: posterSize,
                            minHeight: posterSize,
                            width: posterSize,
                            height: posterSize
                        }}
                    >
                        <div className="poster-image-container">
                            <img 
                                src={poster.image || '/api/placeholder/400/400'} 
                                alt="Poster"
                                className="poster-image"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '12px'
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <section className="poster-carousel-section">
            <div className="poster-carousel-header">
                <div className="header-container">
                    <h1 className={windowWidth <= 768 ? "mobile-heading" : ""}>
                        Our Students Achievements
                    </h1>
                    <span className="subheading">Our Students, Our Pride</span>
                </div>
            </div>
            <div 
                className="carousel-container"
                ref={carouselContainerRef}
                style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',  // Changed back to hidden
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverscrollBehaviorX: 'contain',
                    overscrollBehaviorX: 'contain',
                    cursor: isScrolling ? 'grabbing' : 'grab',
                    padding: '20px 0',
                    position: 'relative',
                    height: `calc(${getPosterSize()} + 100px)`, // Fixed height
                    marginBottom: '50px'  // Added margin for shadow
                }}
            >
                <div 
                    className="carousel-wrapper"
                    style={{
                        userSelect: 'none',
                        paddingBottom: '20px', // Reduced padding
                        paddingTop: '20px',    // Reduced padding
                        position: 'relative'    // Added position relative
                    }}
                >
                    <div 
                        className={`carousel-row ${windowWidth <= 768 ? "mobile-row" : ""}`}
                        style={{ 
                            minHeight: getPosterSize(),
                            height: getPosterSize(),
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {renderPosterCards(posters)}
                    </div>
                </div>
            </div>
            <style>
                {`
                /* Hide scrollbar for Chrome, Safari and Opera */
                .carousel-container::-webkit-scrollbar {
                    display: none;
                }
                
                /* Ensure the container is scrollable horizontally only */
                .carousel-container {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                    overflow-x: auto;
                    overflow-y: hidden;
                    position: relative;
                }
                
                /* Fix the carousel track */
                .carousel-track {
                    will-change: transform;
                    transform: translateZ(0);
                    display: flex;
                    align-items: center;
                    position: relative;
                }
                
                /* Ensure posters are fully visible */
                .poster-card {
                    flex-shrink: 0;
                    position: relative;
                    overflow: visible;
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
                    margin: 0 1rem 30px 1rem; /* Added bottom margin */
                }
                
                /* Override any min-height constraints that might be causing cutoff */
                .carousel-row {
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: visible !important;
                }
                
                /* Make sure poster carousel section has enough space */
                .poster-carousel-section {
                    padding: 2rem 0 5rem 0 !important;
                    position: relative;
                    overflow: visible !important;
                    margin-bottom: 70px; /* Increased bottom margin */
                }

                .carousel-wrapper {
                    padding-bottom: 40px !important; /* Increased padding */
                }
                
                @media screen and (max-width: 768px) {
                    .carousel-container {
                        padding: 10px 0 20px 0; /* Reduced bottom padding */
                        margin-bottom: 20px;    /* Reduced bottom margin */
                    }
                    
                    .poster-card {
                        margin: 0 0.5rem 10px 0.5rem; /* Reduced bottom margin */
                    }

                    .poster-carousel-section {
                        padding: 1rem 0 2rem 0 !important; /* Reduced padding */
                        margin-bottom: 30px;              /* Reduced margin */
                    }

                    .carousel-wrapper {
                        padding-bottom: 20px !important; /* Reduced padding */
                    }
                }
                `}
            </style>
        </section>
    );
};

export default PosterCarousel;